import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import type {ProjectPickerBaseProject} from '@github-ui/item-picker-project'
import {ProjectPicker, type ProjectPickerProps} from '@github-ui/item-picker-project-tsq'
import {useCallback} from 'react'

import {useUpdateIssueProjectBulkMutation} from './mutations/use-update-issue-project-bulk-mutation'

export type BulkIssueProjectPickerProps = Omit<ProjectPickerProps, 'onSave'> &
  Omit<SharedBulkActionsItemPickerProps, 'issuesToActOn'> &
  Parameters<typeof useUpdateIssueProjectBulkMutation>[0] & {
    issueIds: string[]
  }

export function BulkIssueProjectPicker({
  onError,
  onCompleted,
  onMutate,
  issueIds,
  selectedProjects,
  ...rest
}: BulkIssueProjectPickerProps) {
  const {mutate: bulkUpdateMutate} = useUpdateIssueProjectBulkMutation({onMutate})
  const onSave = useCallback(
    (newSelectedProjects: ProjectPickerBaseProject[]) => {
      const projectsToAdd = newSelectedProjects.filter(a => !selectedProjects.some(aa => aa.id === a.id))
      const projectsToRemove = selectedProjects.filter(
        (project): project is ProjectPickerBaseProject & {id: string} => {
          if (!project.id) return false
          return !newSelectedProjects.some(candidate => candidate.id === project.id)
        },
      )

      if (!projectsToAdd.length && !projectsToRemove.length) {
        return
      }

      const ids = [...issueIds]
      const input = {
        ids,
        addToProjectIds: projectsToAdd.map(p => p.id),
        removeFromProjectIds: projectsToRemove.map(p => (p.id ? p.id.toString() : '')),
        projects: newSelectedProjects,
      }

      bulkUpdateMutate(input, {
        onSuccess: response => onCompleted?.(response.data.updateIssuesBulk?.jobId ?? undefined),
        onError,
      })
    },
    [bulkUpdateMutate, issueIds, onCompleted, onError, selectedProjects],
  )

  return <ProjectPicker onSave={onSave} selectedProjects={selectedProjects} {...rest} />
}
